import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck, ChevronDown, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API, LINKS, trackEvent } from "../../data";
import { CONTACT, CATALOG } from "./catalog";

const EASE = [0.22, 1, 0.36, 1];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const darkInput =
  "rounded-xl bg-white/10 px-4 py-3 text-sm text-white ring-1 ring-white/25 placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-sky";

export function ContactSection({ lang, prefill, detailed, s }) {
  const f = CONTACT[lang];
  const c = CATALOG[lang];
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "", service: "", location: "", message: "", consent: false });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showDetailed, setShowDetailed] = useState(false);

  useEffect(() => {
    if (!prefill) return;
    setForm((p) => ({
      ...p,
      interest: prefill.interest || p.interest,
      service: prefill.service || p.service,
    }));
    if (prefill.openDetailed) setShowDetailed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (form.name.trim().length < 2) er.name = f.required;
    const hasPhone = form.phone.trim().length >= 5;
    const hasEmail = EMAIL_RE.test(form.email.trim());
    if (!hasPhone && !hasEmail) er.contact = f.contactError;
    if (form.email.trim() && !hasEmail) er.email = f.emailError;
    if (!form.interest) er.interest = f.required;
    if (form.interest === f.singleService && !form.service) er.service = f.required;
    if (!form.consent) er.consent = f.consentError;
    setErrors(er);
    if (Object.keys(er).length) return;
    setSending(true);
    try {
      const parts = [
        form.phone.trim() && `Telefono: ${form.phone.trim()}`,
        form.email.trim() && `Email: ${form.email.trim()}`,
        form.location.trim() && `Località: ${form.location.trim()}`,
        form.message.trim() && form.message.trim(),
      ].filter(Boolean);
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim() || "nondisponibile@ogstay.com",
        subject: lang === "en"
          ? `Interest: ${form.interest}${form.service ? ` — ${form.service}` : ""}`
          : `Interesse: ${form.interest}${form.service ? ` — ${form.service}` : ""}`,
        message: parts.join("\n") || "-",
        locale: lang,
      });
      trackEvent("owner_lead", { interest: form.interest, service: form.service || undefined, source: "servizi_contatto" });
      setSent(true);
      toast.success(f.success);
    } catch {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  const err = (k) =>
    errors[k] && (
      <p data-testid={`ct-error-${k}`} role="alert" className="text-xs font-bold text-red-300">
        {errors[k]}
      </p>
    );
  const labelCls = "text-xs font-extrabold uppercase tracking-[0.15em] text-sky";
  const waTopic = form.interest === f.singleService && form.service ? form.service : form.interest;

  return (
    <section id="contatto" data-testid="contact-section" className="bg-gradient-to-b from-navy to-med px-5 py-24 text-white sm:px-8 sm:py-32">
      <span id="valutazione" className="absolute" aria-hidden />
      <div className="mx-auto grid max-w-6xl items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <h2 className="font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl" data-testid="contact-title">
            {s.final.title}
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-white/70 sm:text-base">{s.final.sub}</p>
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
            {s.final.trust.map((item) => (
              <span key={item} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60">
                <BadgeCheck className="h-4 w-4 text-sky" />
                {item}
              </span>
            ))}
          </div>

          <div className="mt-10 space-y-3">
            <a
              data-testid="contact-whatsapp-cta"
              href={`${LINKS.whatsapp}?text=${encodeURIComponent(f.waMessage(waTopic))}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click_owner", { topic: waTopic || "generale", source: "servizi_contatto" })}
              className="flex items-center justify-center gap-3 rounded-full bg-sky px-7 py-4 text-sm font-extrabold text-navy transition-transform duration-300 hover:scale-[1.02]"
            >
              {f.whatsapp}
            </a>
            <a
              data-testid="contact-phone-cta"
              href={LINKS.phone}
              onClick={() => trackEvent("phone_click", { source: "servizi_contatto" })}
              className="flex items-center justify-center gap-3 rounded-full px-7 py-4 text-sm font-extrabold text-white ring-1 ring-white/30 transition-colors hover:bg-white/10"
            >
              <Phone className="h-4 w-4" />
              {f.call}
            </a>
            <p className="pt-2 text-center text-xs font-semibold uppercase tracking-[0.16em] text-white/45">{f.orForm}</p>
          </div>
        </div>

        <div>
          {sent ? (
            <div data-testid="ct-success-panel" className="rounded-[2rem] bg-white/10 p-10 text-center ring-1 ring-white/15">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sky/20 text-sky">
                <BadgeCheck className="h-7 w-7" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-semibold">{f.successTitle}</h3>
              <p className="mx-auto mt-3 max-w-md text-sm text-white/70">{f.successBody}</p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} noValidate className="grid gap-4 rounded-[2rem] bg-white/5 p-7 ring-1 ring-white/10 sm:p-8">
              <div className="grid gap-2">
                <label className="grid gap-2">
                  <span className={labelCls}>{f.name} *</span>
                  <input data-testid="ct-name-input" name="name" autoComplete="name" value={form.name} onChange={set("name")} placeholder={f.namePh} className={darkInput} />
                </label>
                {err("name")}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <label className="grid gap-2">
                    <span className={labelCls}>{f.phone}</span>
                    <input data-testid="ct-phone-input" name="tel" autoComplete="tel" type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder={f.phonePh} className={darkInput} />
                  </label>
                </div>
                <div className="grid gap-2">
                  <label className="grid gap-2">
                    <span className={labelCls}>{f.email}</span>
                    <input data-testid="ct-email-input" name="email" autoComplete="email" type="email" inputMode="email" value={form.email} onChange={set("email")} placeholder={f.emailPh} className={darkInput} />
                  </label>
                  {err("email")}
                </div>
              </div>
              {err("contact")}
              <div className="grid gap-2">
                <label className="grid gap-2">
                  <span className={labelCls}>{f.interest} *</span>
                  <select data-testid="ct-interest-select" name="interest" value={form.interest} onChange={set("interest")} className={`${darkInput} appearance-none`}>
                    <option value="" disabled className="text-navy">—</option>
                    {f.interests.map((opt) => (
                      <option key={opt} value={opt} className="text-navy">{opt}</option>
                    ))}
                  </select>
                </label>
                {err("interest")}
              </div>
              <AnimatePresence initial={false}>
                {form.interest === f.singleService && (
                  <motion.div
                    key="service-select"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="grid gap-2">
                      <label className="grid gap-2">
                        <span className={labelCls}>{f.service} *</span>
                        <select data-testid="ct-service-select" name="service" value={form.service} onChange={set("service")} className={`${darkInput} appearance-none`}>
                          <option value="" disabled className="text-navy">—</option>
                          {c.services.map((sv) => (
                            <option key={sv.id} value={sv.title} className="text-navy">{sv.title}</option>
                          ))}
                        </select>
                      </label>
                      {err("service")}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <label className="grid gap-2">
                <span className={labelCls}>{f.location}</span>
                <input data-testid="ct-location-input" name="location" value={form.location} onChange={set("location")} placeholder={f.locationPh} className={darkInput} />
              </label>
              <label className="grid gap-2">
                <span className={labelCls}>{f.message}</span>
                <textarea data-testid="ct-message-input" name="message" rows={3} value={form.message} onChange={set("message")} placeholder={f.messagePh} className={`${darkInput} resize-none`} />
              </label>
              <div className="grid gap-2">
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-white/70">
                  <input
                    data-testid="ct-consent-checkbox"
                    type="checkbox"
                    checked={form.consent}
                    onChange={set("consent")}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-sky"
                  />
                  <span>
                    {f.consent.split("Privacy Policy")[0]}
                    <Link to="/privacy-policy" className="font-bold text-sky underline underline-offset-2">
                      Privacy Policy
                    </Link>
                    {f.consent.split("Privacy Policy")[1]}
                  </span>
                </label>
                {err("consent")}
              </div>
              <button data-testid="ct-submit-button" type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-60">
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? f.sending : f.submit}
              </button>
            </form>
          )}

          {detailed && (
            <div className="mt-6">
              <button
                type="button"
                data-testid="detailed-form-toggle"
                aria-expanded={showDetailed}
                onClick={() => setShowDetailed((v) => !v)}
                className="flex w-full items-center justify-between gap-4 rounded-2xl bg-white/5 px-6 py-4 text-left ring-1 ring-white/10 transition-colors hover:bg-white/10"
              >
                <span>
                  <span className="block text-sm font-bold text-white">{f.detailedTitle}</span>
                  <span className="mt-1 block text-xs text-white/55">{f.detailedSub}</span>
                </span>
                <ChevronDown className={`h-5 w-5 shrink-0 text-sky transition-transform duration-300 ${showDetailed ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {showDetailed && (
                  <motion.div
                    data-testid="detailed-form-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="pt-6">{detailed}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
